/**
 * Product Context
 *
 * Provides centralized product state management including:
 * - Product fetching with caching
 * - Recently viewed products tracking
 * - Product pre-loading strategy
 * - LocalStorage persistence for history
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';
import { productService } from '../services/api';
import type { ApiError, Product } from '../services/api/types';

/**
 * Product context value
 */
export interface ProductContextValue {
  // Current product state
  currentProduct: Product | null;
  recentlyViewedProducts: Product[];

  // UI state
  loading: boolean;
  error: ApiError | null;

  // Actions
  fetchProduct: (productId: string) => Promise<void>;
  addToRecentlyViewed: (product: Product) => void;
  clearRecentlyViewed: () => void;
  clearError: () => void;

  // Utilities
  hasRecentlyViewedProducts: boolean;
  getRecentlyViewedIds: () => string[];
}

/**
 * Storage key for recently viewed products
 */
const RECENTLY_VIEWED_KEY = 'quiz_commerce_recently_viewed';

/**
 * Maximum number of recently viewed products to store
 */
const MAX_RECENTLY_VIEWED = 10;

/**
 * Product cache entry
 */
interface CacheEntry {
  product: Product;
  timestamp: number;
}

/**
 * Cache duration (5 minutes)
 */
const CACHE_DURATION = 5 * 60 * 1000;

/**
 * Type guard for ApiError
 */
function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error
  );
}

/**
 * Convert unknown error to ApiError
 */
function toApiError(error: unknown): ApiError {
  if (isApiError(error)) {
    return error;
  }

  if (error instanceof Error) {
    return {
      code: 'UNKNOWN_ERROR',
      message: error.message,
    };
  }

  return {
    code: 'UNKNOWN_ERROR',
    message: 'An unexpected error occurred',
  };
}

/**
 * Load recently viewed products from localStorage
 */
function loadRecentlyViewed(): Product[] {
  try {
    const stored = localStorage.getItem(RECENTLY_VIEWED_KEY);
    if (!stored) return [];

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- JSON.parse returns any, validated through Product[] type
    const products: Product[] = JSON.parse(stored);
    return Array.isArray(products) ? products : [];
  } catch {
    return [];
  }
}

/**
 * Save recently viewed products to localStorage
 */
function saveRecentlyViewed(products: Product[]): void {
  try {
    localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(products));
  } catch (err) {
    console.error('Failed to save recently viewed products:', err);
  }
}

/**
 * Product Context
 */
const ProductContext = createContext<ProductContextValue | undefined>(undefined);

/**
 * Product Provider Props
 */
export interface ProductProviderProps {
  children: ReactNode;
}

/**
 * Product Provider Component
 */
export const ProductProvider: React.FC<ProductProviderProps> = ({ children }) => {
  const [currentProduct, setCurrentProduct] = useState<Product | null>(null);
  const [recentlyViewedProducts, setRecentlyViewedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  // Product cache: Map<productId, CacheEntry>
  const [productCache] = useState<Map<string, CacheEntry>>(new Map());

  /**
   * Load recently viewed products from localStorage on mount
   */
  useEffect(() => {
    const products = loadRecentlyViewed();
    setRecentlyViewedProducts(products);
  }, []);

  /**
   * Check if product is in cache and not expired
   */
  const getCachedProduct = useCallback(
    (productId: string): Product | null => {
      const cached = productCache.get(productId);
      if (!cached) return null;

      const isExpired = Date.now() - cached.timestamp > CACHE_DURATION;
      if (isExpired) {
        productCache.delete(productId);
        return null;
      }

      return cached.product;
    },
    [productCache]
  );

  /**
   * Cache product data
   */
  const cacheProduct = useCallback(
    (product: Product): void => {
      productCache.set(product.id, {
        product,
        timestamp: Date.now(),
      });
    },
    [productCache]
  );

  /**
   * Add product to recently viewed list
   */
  const addToRecentlyViewed = useCallback((product: Product): void => {
    setRecentlyViewedProducts((prev) => {
      // Remove if already exists
      const filtered = prev.filter((p) => p.id !== product.id);

      // Add to beginning
      const updated = [product, ...filtered];

      // Limit to MAX_RECENTLY_VIEWED
      const limited = updated.slice(0, MAX_RECENTLY_VIEWED);

      // Persist to localStorage
      saveRecentlyViewed(limited);

      return limited;
    });
  }, []);

  /**
   * Fetch product by ID
   */
  const fetchProduct = useCallback(
    async (productId: string): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        // Check cache first
        const cached = getCachedProduct(productId);
        if (cached) {
          setCurrentProduct(cached);
          setLoading(false);
          return;
        }

        // Fetch from API
        const response = await productService.getProductDetails(productId);

        if (!response.success || !response.data) {
          throw response.error || new Error('Failed to fetch product');
        }

        const product = response.data;

        // Update cache and state
        cacheProduct(product);
        setCurrentProduct(product);

        // Add to recently viewed
        addToRecentlyViewed(product);

        setLoading(false);
      } catch (err) {
        const apiError = toApiError(err);
        setError(apiError);
        setLoading(false);
      }
    },
    [getCachedProduct, cacheProduct, addToRecentlyViewed]
  );

  /**
   * Clear recently viewed products
   */
  const clearRecentlyViewed = useCallback((): void => {
    setRecentlyViewedProducts([]);
    localStorage.removeItem(RECENTLY_VIEWED_KEY);
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Get recently viewed product IDs
   */
  const getRecentlyViewedIds = useCallback((): string[] => {
    return recentlyViewedProducts.map((p) => p.id);
  }, [recentlyViewedProducts]);

  /**
   * Context value
   */
  const contextValue: ProductContextValue = {
    currentProduct,
    recentlyViewedProducts,
    loading,
    error,
    fetchProduct,
    addToRecentlyViewed,
    clearRecentlyViewed,
    clearError,
    hasRecentlyViewedProducts: recentlyViewedProducts.length > 0,
    getRecentlyViewedIds,
  };

  return <ProductContext.Provider value={contextValue}>{children}</ProductContext.Provider>;
};

/**
 * Custom hook to use product context
 */
export const useProduct = (): ProductContextValue => {
  const context = useContext(ProductContext);

  if (!context) {
    throw new Error('useProduct must be used within ProductProvider');
  }

  return context;
};
